import React, { useContext, useEffect, useRef, useState } from "react";
import "./styles.css";
import { Breadcrumb, RippleButton, Title } from "../../Components/Atoms";
import PATHS from "../../Routes/paths";
import { useNavigate, useSearchParams } from "react-router-dom";
import { DatePicker } from "../../Components/Molecules";
import { Button } from "@chakra-ui/react";
import { ArrowRightIcon, DownloadIcon } from "../../Assets/images/svgs";
import { secondsToHms, stampToDate } from "../../Library/datetime";
import { getRoutes, getRouteStats } from "../../APIs";
import { DATE_RANGES } from "../../Library/constants";
import { StoreContext } from "../../Library/contexts";
import { jsonToCSVConvertor } from "../../Library/jsonUtils";

const VehicleRouteSummary = () => {
  const [ searchParams ] = useSearchParams();
  const navigate = useNavigate();
  const { filters, allVehicleRoutes, setAllVehicleRoutes } = useContext(StoreContext);
  const [ vehRoutes, setVehRoutes ] = useState([]);
  const [ processedStats, setProcessedStats ] = useState(null);
  const selectedDateDataMap = useRef({});

  const breadcrumbPaths = [
    { idx: 1, name: "Summary", link: PATHS.VIEW_ROUTES },
    { idx: 2, name: `${searchParams.get("reg") || ""} Routes`, isCurrentPage: true },
  ];

  const vehicleTripDets = [
    { label: "Routes", key: "routeCount" },
    { label: "Total Trips", key: "tripCount" },
    { label: "Expected Route Dist.", key: "totalExpectedDist", dist: true },
    { label: "Actual Route Dist.", key: "totalActualDist", dist: true },
    { label: "Deviated Dist.", key: "totalDeviatedDist", dist: true, deviated: true },
    { label: "Total Distance", key: "totalTravelledDist", dist: true  },
    { label: "Total Duration", key: "totalTravelledDur", ts: true },
    { label: "Running Duration", key: "totalRunningDur", ts: true  },
    { label: "Avg Speed", key: "averageSpd", speed: true },
    { label: "Top Speed", key: "topSpd", speed: true },
  ];

  const getDisplayDurWoSec = (ts) => {
    let timeObj = secondsToHms(ts, true);
    return `${timeObj.h} h ${timeObj.m} m`;
  }

  const processFetchedStats = (routeStats) => {
    const finalSummary = routeStats.reduce((acc, { dayStats, routes = [] }) => {
      routes.forEach(({ routeId, trips, routeDst, dst, deviated, dur }) => {
        if (!acc.routeMap[routeId]) {
          acc.routeMap[routeId] = { routeId, trips: 0, routeDst: 0, dst: 0, deviated: 0, dur: 0 };
        }
        const r = acc.routeMap[routeId];
        r.trips += trips;
        r.routeDst += routeDst * trips;
        r.dst += dst;
        r.deviated += deviated;
        r.dur += dur;

        acc.totalTripCount += trips;
        acc.totalExpectedDist += routeDst * trips;
        acc.totalActualDist += dst;
        acc.totalRouteDur += dur;
        acc.totalDeviatedDist += deviated;
      });

      acc.totalTravelledDist += dayStats.dst || 0;
      acc.totalTravelledDur += dayStats.dur || 0;
      acc.totalRunningDur += dayStats.runningTime || 0;

      if (dayStats.avs) {
        acc.spdSum += dayStats.avs;
        acc.validAvgSpdCount += 1;
      }

      acc.topSpd = Math.max(acc.topSpd, dayStats.tps || 0);

      return acc;
    }, {
      routeMap: {}, totalTripCount: 0, totalExpectedDist: 0, totalActualDist: 0,
      totalDeviatedDist: 0, totalTravelledDist: 0, totalTravelledDur: 0,
      totalRunningDur: 0, topSpd: 0, spdSum: 0, validAvgSpdCount: 0
    });

    const mergedRoutes = Object.values(finalSummary.routeMap);
    const averageSpd = finalSummary.validAvgSpdCount ? (finalSummary.spdSum / finalSummary.validAvgSpdCount).toFixed(2) : 0;

    const summaryObject = {
      routeCount: mergedRoutes.length,
      tripCount: finalSummary.totalTripCount,
      totalExpectedDist: finalSummary.totalExpectedDist,
      totalActualDist: finalSummary.totalActualDist,
      totalDeviatedDist: finalSummary.totalDeviatedDist,
      totalTravelledDist: finalSummary.totalTravelledDist,
      totalTravelledDur: finalSummary.totalTravelledDur,
      totalRunningDur: finalSummary.totalRunningDur,
      averageSpd,
      topSpd: finalSummary.topSpd,
      mergedRoutes: mergedRoutes
    };
    setProcessedStats(summaryObject);
  }

  useEffect(() => {
    let selectedDate = DATE_RANGES[filters.range];
    if (selectedDateDataMap.current[filters.range] && selectedDateDataMap.current[filters.range].length) return processFetchedStats(selectedDateDataMap.current[filters.range]);

    getRouteStats({ fromTs: selectedDate.from, toTs: selectedDate.to, vids: [ +searchParams.get("idx") ] }, (err, routeStats) => {
      if (err) console.log(err);
      else {
        selectedDateDataMap.current[filters.range] = routeStats;
        getRoutes((err, data) => {
          if (err) console.error(err);
          else {
            const activeRouteIdxSet = new Set(data.filter(rt => !rt.archived).map(route => route.idx));
            const filteredRouteStats = routeStats
              .map(stat => {
                const filteredRoutes = stat.routes.filter(r => activeRouteIdxSet.has(r.routeId));

                return {
                  ...stat,
                  routes: filteredRoutes,
                };
              })
              .filter(stat => stat.routes.length > 0);

            processFetchedStats(filteredRouteStats);
            setAllVehicleRoutes(data);
          }
        });
      }
    }, []);
  }, [ filters.range ]);

  useEffect(() => {
    if (!processedStats || !processedStats.mergedRoutes || !processedStats.mergedRoutes.length) return;

    let routeNameMap = {};
    allVehicleRoutes.forEach(route => {
      routeNameMap[route.idx] = route.nm;
    });
    let mergedRoutesWithName = processedStats.mergedRoutes.map(route => ({ ...route, nm: routeNameMap[route.routeId] }));
    setVehRoutes(mergedRoutesWithName);

  }, [ processedStats, allVehicleRoutes ]);

  const routeCardDetails = [
    { label: "No. of Trips", key: "trips" },
    { label: "Expected Route Dist.", key: "routeDst", dst: true },
    { label: "Actual Route Dist.", key: "dst", dst: true },
    { label: "Deviated Dist.", key: "deviated", dst: true, deviated: true },
    { label: "Total Duration", key: "dur", ts: true },
  ];

  const handleReportDownload = () => {
    const getdst = (dst)=> {return `${(dst/1000).toFixed(1)} kms`};
    let dur = 0;
    var sendToExcel = processedStats.mergedRoutes.map(a => Object.assign({}, a));
    sendToExcel.forEach((stat, idx) => {
      stat["Sr No"] = idx + 1;
      stat["Registration Number"] = searchParams.get("reg") || "-";
      stat["Trips"] = stat.trips || "-";
      stat["Expected Route Distance"] = stat.routeDst ? getdst(stat.routeDst) : "-";
      stat["Actual Route Distance"] = stat.dst ? getdst(stat.dst) : "-";
      stat["Deviated Distance"] = stat.deviated ? getdst(stat.deviated) : "-";
      stat["Total Duration"] = stat.dur ? secondsToHms(stat.dur) : "-";
      dur += stat.dur;
    });

    sendToExcel.push({ "Sr No": "", "Registration Number": "", "Trips": "", "Expected Route Distance": "", "Actual Route Distance": "", "Deviated Distance": "", "Total Duration": "" });
    sendToExcel.push({ "Sr No": "", "Registration Number": "Total", "Trips": processedStats.tripCount, "Expected Route Distance": getdst(processedStats.totalExpectedDist), "Actual Route Distance": getdst(processedStats.totalActualDist), "Deviated Distance": getdst(processedStats.totalDeviatedDist), "Total Duration": secondsToHms(dur) });

    var sendData = sendToExcel.map(function (exceldata) {
      var args = "Sr No, Registration Number, Trips, Expected Route Distance, Actual Route Distance, Deviated Distance, Total Duration";
      var filteredData = args.split(",").reduce((mem, arg) => {
        arg = arg.trim();
        mem[arg] = exceldata[arg];
        return mem;
      }, {});
      return filteredData;
    });
    if (sendData.length) jsonToCSVConvertor(sendData, "", true, `Routes_Report_` + stampToDate(DATE_RANGES[filters.range].from) + "_" + stampToDate(DATE_RANGES[filters.range].to), false);
  }

  return (
    <div className="panel-main vehicle-route-summary">
      {processedStats ? <>
        <div className="vehicle-route-summary-header">
          <Breadcrumb paths={breadcrumbPaths} />
          <DatePicker />
        </div>

        <div className="vehicle-route-data-card">
          <div className="vehicle-route-data-card-header">
            <p>{searchParams.get("reg")}</p>
            <RippleButton onClick={() => handleReportDownload()}>
              <DownloadIcon />
              Download Report
            </RippleButton>
          </div>
          <div className="vehicle-route-details">
            {vehicleTripDets.map(det => (
              <div className="route-det-container">
                <p className="route-det-label">{det.label || "-"}</p>
                <p className={`route-det-value ${det.deviated ? "highlight-val" : ""}`}>{det.ts ? getDisplayDurWoSec(processedStats[det.key]) : (det.dist ? (processedStats[det.key]/1000).toFixed(1) : processedStats[det.key] || 0)} {det.dist ? "kms" : (det.speed ? "km/hr" : "")}</p>
              </div>
            ))}
          </div>
        </div>

        {vehRoutes && vehRoutes.length ? <div className="vehicle-travelled-routes-container">
          <div className="vehicle-routes-header">
            <Title>
              Routes
            </Title>
          </div>
          <div className="vehicle-route-cards-container">
            {vehRoutes.map(route => (
              <div className="vehicle-route-card">
                <div className="veh-route-card-header">
                  <p className="veh-route-name">{route.nm || "-"}</p>
                  {/* <RippleButton variant={"unstyled"} className="veh-route-view-btn">
                    View Route
                  </RippleButton> */}
                </div>
                <div className="veh-route-details-container">
                  {routeCardDetails.map(dets => (
                    <div className="route-detail-box">
                      <p className="route-detail-label">{dets.label || "-"}</p>
                      <p className={`route-detail-value ${dets.deviated ? "highlight-text" : ""}`}>{dets.ts ? getDisplayDurWoSec(route[dets.key]) : (dets.dst ? (route[dets.key]/1000).toFixed(1) : route[dets.key]) || "-"} {dets.dst ? "kms" : ""}</p>
                    </div>
                  ))}
                </div>
                <div className="view-trip-btn-container">
                  <RippleButton variant={"unstyled"} className="veh-route-view-trip-btn" onClick={() => {
                    let trips = [];
                    if (selectedDateDataMap.current[filters.range]) {
                      trips = selectedDateDataMap.current[filters.range].reduce((acc, curr) => {
                        let matchedRoute = curr.routes.find(r => r.routeId === route.routeId);
                        if (matchedRoute && matchedRoute.tripStats) acc = acc.concat(matchedRoute.tripStats);
                        return acc;
                      }, []);
                    }
                    navigate(`${PATHS.VIEW_ROUTES}/${PATHS.PAST_RIDES}/${searchParams.get("idx")}?route=${route.routeId}&reg=${searchParams.get("reg")}`, { state: { trips } });
                  }}>
                    View trip details
                    <ArrowRightIcon />
                  </RippleButton>
                </div>
              </div>
            ))}
          </div>
        </div> : null}
      </> : null}
    </div>
  )
}

export default VehicleRouteSummary
